import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import {
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import { updateCollision } from '../../state/slices/collisionSlice';
import { Collision } from '../../models/collision';

interface VehicleConditionComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

const vehicleConditionMapping = [
  {
    label: 'Defective brakes',
    eCollisionLabel: 'Defective Brakes',
  },
  {
    label: 'Tire failure',
    eCollisionLabel: 'Tires Failed',
  },
  {
    label: 'Improper load or attachment',
    eCollisionLabel: 'Improper load or attachment',
  },
  {
    label: 'Broken or defective lights',
    eCollisionLabel: 'Broken or defective lights',
  },
];

export const VehicleConditionComponent = ({
  navigationConfiguration,
}: VehicleConditionComponentProps) => {
  const dispatch = useAppDispatch();

  const radioGroupFormError = 'Choose an answer';
  const dropDownFormError = 'Choose a vehicle condition';
  const bannerErrorText = 'Make a selection below.';

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const [vehicleConditionSelection, setVehicleConditionSelection] = useState(
    stateCollision?.collision?.vehicles?.[0]?.vehicleCondition ?? null,
  );
  const [vehicleConditionGroupFormError, setVehicleConditionDropGroupError] =
    useState<boolean>(false);

  const [nextPage, setNextPage] = useState('');

  useEffect(() => {
    setNextPage(
      navigationConfiguration.pagingConfiguration.find(
        (item) => item.default !== undefined,
      )?.default ?? 'error',
    );
  }, []);

  const validate = (): boolean => {
    let isValid = true;

    dispatch(removeAllValidationErrorNotifications());

    if (vehicleConditionSelection === null) {
      setVehicleConditionDropGroupError(true);
      isValid = false;
    }

    if (!isValid) {
      dispatch(
        pushNotification({
          body: bannerErrorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
    }

    return isValid;
  };

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        true,
        false,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    updatedCollision.vehicles = [
      {
        ...stateCollision?.collision?.vehicles![0],
        vehicleCondition: vehicleConditionSelection
          ? vehicleConditionSelection
          : null,
      },
    ];

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <h2>Vehicle condition</h2>
      <GoASpacer vSpacing={'l'} />
      <p>Please indicate an answer below.</p>

      <GoAFormItem
        label="Which vehicle condition was the contributing factor in your collision?"
        labelSize="regular"
        error={vehicleConditionGroupFormError ? dropDownFormError : ''}
      >
        <GoARadioGroup
          name="trafficControlDeviceGroup"
          value={
            vehicleConditionSelection != null
              ? vehicleConditionSelection.toString()
              : ''
          }
          error={vehicleConditionGroupFormError}
          onChange={(_, value) => {
            setVehicleConditionDropGroupError(false);
            dispatch(removeAllValidationErrorNotifications());
            const selection = parseInt(value.toString());
            setVehicleConditionSelection(selection);
          }}
        >
          {vehicleConditionMapping.map((condition) => {
            const id = stateConfiguration.configuration.vehicleCondition
              .find(
                (x) =>
                  x.value.toLowerCase() ===
                  condition.eCollisionLabel.toLowerCase(),
              )
              ?.id.toString();

            return (
              <GoARadioItem key={id} value={id ?? ''} label={condition.label} />
            );
          })}
        </GoARadioGroup>
      </GoAFormItem>

      <WizardButtonGroup
        nextPage={nextPage}
        validate={validate}
        save={save}
        goToReview={navigationConfiguration.goToReview}
        navigate={navigationConfiguration.navigate}
        buttonText={'Save and continue'}
      />
    </div>
  );
};
